import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Check, X, Trash2, Clock, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchReviews();
  }, [isAuthenticated, navigate, statusFilter]);

  const fetchReviews = async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/reviews/admin`
        : `${API_URL}/api/reviews/admin?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Ошибка загрузки отзывов');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reviewId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Статус отзыва изменён на "${newStatus === 'approved' ? 'одобрен' : 'отклонён'}"`);
        fetchReviews();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast.error('Ошибка обновления статуса');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Удалить этот отзыв?')) return;

    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Отзыв удалён');
        fetchReviews();
      } else {
        throw new Error('Failed to delete review');
      }
    } catch (error) {
      toast.error('Ошибка удаления отзыва');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Одобрен</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Отклонён</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> На модерации</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = reviews.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">Админ-панель</h1>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate('/admin/orders')}>
                  Заказы
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/products')}>
                  Товары
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/gallery')}>
                  Галерея
                </Button>
                <Button variant="outline" className="border-emerald-600 text-emerald-700" onClick={() => navigate('/admin/reviews')}>
                  Отзывы {pendingCount > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{pendingCount}</Badge>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Фильтр:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все отзывы</SelectItem>
                <SelectItem value="pending">На модерации</SelectItem>
                <SelectItem value="approved">Одобренные</SelectItem>
                <SelectItem value="rejected">Отклонённые</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500">
              Всего: {reviews.length} отзывов
            </span>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-500 mt-4">Загрузка...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Отзывов пока нет</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-gray-900">{review.name}</span>
                      <span className="text-gray-500 text-sm">{review.location}</span>
                      {getStatusBadge(review.status)}
                    </div>
                    
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>

                    <p className="text-gray-700 mb-3">{review.text}</p>

                    {review.product && (
                      <p className="text-sm text-emerald-600 mb-2">Товар: {review.product}</p>
                    )}

                    <p className="text-xs text-gray-400">{formatDate(review.created_at)}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {review.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateStatus(review.id, 'approved')}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Одобрить
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => updateStatus(review.id, 'rejected')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Отклонить
                        </Button>
                      </>
                    )}
                    {review.status === 'rejected' && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => updateStatus(review.id, 'approved')}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Одобрить
                      </Button>
                    )}
                    {review.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(review.id, 'rejected')}
                      >
                        Скрыть
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => deleteReview(review.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReviewsPage;
